/**
 * iter87.199.22 — Leaderboard restyle preview (temporary route).
 *
 * Four visual directions rendered side-by-side against the same mock
 * data so the operator can pick one before we wire the real
 * LeaderboardTable.jsx. All variants keep the same information
 * hierarchy (rank chip, name, my-tag, PTS trailing) and only diverge
 * on tone / glow / motion.
 *
 * Delete this file after the operator picks and we ship the chosen
 * variant into LeaderboardTable.
 */
import { useState } from "react";
import { Crown, Flame, Sparkles, Trophy } from "lucide-react";
import { Link } from "react-router-dom";

const MOCK_ROWS = [
    { rank: 1, name: "ds", pts: 14, isMe: false, streak: 3 },
    { rank: 2, name: "MarMarDuffy", pts: 12, isMe: false, streak: 0 },
    { rank: 3, name: "tka", pts: 11, isMe: true, streak: 1 },
    { rank: 4, name: "westy", pts: 9, isMe: false, streak: 0 },
    { rank: 5, name: "jrobs", pts: 7, isMe: false, streak: 0 },
];


// ---------- Variant A · Neon Circuit ----------
// Thin bright outline + electric glow shadow, dark row underneath.
// The rank chip has a saturated solid color; row edge carries a
// pulsing accent bar. Reads as a track-lit scoreboard.

function NeonCircuitRow({ row, isTop }) {
    const NEON = {
        1: { border: "border-[#00E5FF]", glow: "shadow-[0_0_24px_-4px_rgba(0,229,255,0.65)]", accent: "bg-[#00E5FF]", text: "text-[#00E5FF]", chipBg: "bg-[#00E5FF]/10" },
        2: { border: "border-[#B8B8FF]", glow: "shadow-[0_0_20px_-6px_rgba(184,184,255,0.55)]", accent: "bg-[#B8B8FF]", text: "text-[#B8B8FF]", chipBg: "bg-[#B8B8FF]/10" },
        3: { border: "border-[#FF6EC7]", glow: "shadow-[0_0_20px_-6px_rgba(255,110,199,0.55)]", accent: "bg-[#FF6EC7]", text: "text-[#FF6EC7]", chipBg: "bg-[#FF6EC7]/10" },
    };
    const s = NEON[row.rank];
    if (!isTop) {
        return (
            <div className={`flex items-center gap-3 px-3 py-2 rounded-xl border ${row.isMe ? "border-cyber/50 bg-cyber/[0.06]" : "border-white/[0.06] bg-[#141414]"}`}>
                <span className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold border border-white/10 text-zinc-500">#{row.rank}</span>
                <span className="text-sm font-bold text-zinc-300 flex-1">{row.name}</span>
                <span className="text-sm font-bold text-zinc-400">{row.pts} <span className="text-[9px] text-zinc-600">PTS</span></span>
            </div>
        );
    }
    return (
        <div className={`relative flex items-center gap-3 px-3 py-2.5 rounded-xl border-2 bg-[#0d0d10] ${s.border} ${s.glow}`}>
            <span className={`absolute left-0 top-2 bottom-2 w-[3px] rounded-r ${s.accent} animate-pulse`} />
            <span className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-black border-2 ${s.border} ${s.chipBg} ${s.text}`}>
                #{row.rank}
            </span>
            <div className="flex-1 min-w-0">
                <div className={`font-black text-base ${s.text}`}>{row.name}</div>
                {row.streak >= 2 && <div className="flex items-center gap-1 text-[10px] text-zinc-500 uppercase tracking-widest"><Flame className="w-3 h-3 text-orange-400" /> W{row.streak}</div>}
            </div>
            <div className={`text-right ${s.text}`}>
                <div className="text-[9px] uppercase tracking-widest opacity-60">PTS</div>
                <div className="text-2xl font-black leading-none">{row.pts}</div>
            </div>
        </div>
    );
}


// ---------- Variant B · Retro Scoreboard ----------
// Solid saturated color blocks + brutalist black borders + shadow-brutal.
// Reads as a chunky 90s ESPN Sunday Countdown ticker.

function RetroScoreboardRow({ row, isTop }) {
    const RETRO = {
        1: { bg: "bg-cyber", text: "text-black", label: "1ST" },
        2: { bg: "bg-zinc-200", text: "text-black", label: "2ND" },
        3: { bg: "bg-blaze", text: "text-black", label: "3RD" },
    };
    const s = RETRO[row.rank];
    if (!isTop) {
        return (
            <div className={`flex items-center gap-3 px-3 py-2 border-2 border-black/60 ${row.isMe ? "bg-cyber/10" : "bg-[#141414]"}`}>
                <span className="w-9 text-center text-[11px] font-bold text-zinc-500 font-mono">#{row.rank}</span>
                <span className="text-sm font-bold text-zinc-300 flex-1">{row.name}</span>
                <span className="text-sm font-bold text-zinc-400 font-mono">{row.pts}</span>
            </div>
        );
    }
    return (
        <div className={`flex items-stretch border-2 border-black shadow-[4px_4px_0_0_rgba(0,0,0,0.9)] ${s.bg} ${s.text}`}>
            <div className="w-16 flex flex-col items-center justify-center border-r-2 border-black py-2">
                <div className="text-[8px] font-black uppercase tracking-widest opacity-80">RANK</div>
                <div className="text-lg font-black leading-none">{s.label}</div>
            </div>
            <div className="flex-1 flex items-center px-3">
                <div className="font-display font-black text-lg uppercase tracking-tight">{row.name}</div>
                {row.streak >= 2 && <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold bg-black/20 rounded px-1.5 py-0.5"><Flame className="w-3 h-3" /> W{row.streak}</span>}
            </div>
            <div className="w-20 flex flex-col items-center justify-center border-l-2 border-black py-2">
                <div className="text-[8px] font-black uppercase tracking-widest opacity-80">PTS</div>
                <div className="text-2xl font-black leading-none font-mono">{row.pts}</div>
            </div>
        </div>
    );
}


// ---------- Variant C · Aurora Halo ----------
// Radial gradient behind each podium row; text stays bright. Leader
// row breathes with a subtle animate-pulse halo.

function AuroraHaloRow({ row, isTop }) {
    const AURORA = {
        1: { grad: "bg-gradient-to-r from-cyber/40 via-yellow-300/15 to-transparent", ring: "ring-2 ring-cyber/70", chip: "bg-cyber text-black", pulse: "shadow-[0_0_32px_-4px_rgba(212,246,68,0.7)]" },
        2: { grad: "bg-gradient-to-r from-cyan-300/40 via-cyan-200/15 to-transparent", ring: "ring-2 ring-cyan-300/70", chip: "bg-cyan-200 text-black", pulse: "" },
        3: { grad: "bg-gradient-to-r from-blaze/40 via-orange-300/15 to-transparent", ring: "ring-2 ring-blaze/70", chip: "bg-blaze text-black", pulse: "" },
    };
    const s = AURORA[row.rank];
    if (!isTop) {
        return (
            <div className={`flex items-center gap-3 px-3 py-2 rounded-xl border ${row.isMe ? "border-cyber/50 bg-cyber/[0.06]" : "border-white/[0.06] bg-[#141414]"}`}>
                <span className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold border border-white/10 text-zinc-500">#{row.rank}</span>
                <span className="text-sm font-bold text-zinc-300 flex-1">{row.name}</span>
                <span className="text-sm font-bold text-zinc-400">{row.pts} <span className="text-[9px] text-zinc-600">PTS</span></span>
            </div>
        );
    }
    return (
        <div className={`relative flex items-center gap-3 px-3 py-2.5 rounded-2xl bg-[#0f0f11] ${s.ring} ${row.rank === 1 ? s.pulse : ""}`}>
            <div className={`absolute inset-0 rounded-2xl ${s.grad} pointer-events-none`} />
            <span className={`relative w-10 h-10 rounded-full flex items-center justify-center text-sm font-black shadow-lg ${s.chip}`}>
                {row.rank === 1 ? <Crown className="w-4 h-4" /> : row.rank === 2 ? <Trophy className="w-4 h-4" /> : "3"}
            </span>
            <div className="relative flex-1 min-w-0">
                <div className="font-black text-base text-white">{row.name}</div>
                {row.streak >= 2 && <div className="flex items-center gap-1 text-[10px] text-orange-300 uppercase tracking-widest font-bold"><Flame className="w-3 h-3" /> {row.streak} in a row</div>}
            </div>
            <div className="relative text-right text-white">
                <div className="text-[9px] uppercase tracking-widest opacity-70">PTS</div>
                <div className="text-2xl font-black leading-none">{row.pts}</div>
            </div>
        </div>
    );
}


// ---------- Variant D · Volt Podium Ribbon ----------
// Left-side vertical color ribbon extends into the row like a
// bookmark. Bright but restrained: keeps the current dark row look
// and adds a saturated color streak + rank badge on the left.

function VoltPodiumRow({ row, isTop }) {
    const RIBBON = {
        1: { bar: "bg-gradient-to-b from-cyber to-yellow-300", chipBorder: "border-cyber", chipText: "text-cyber", chipBg: "bg-cyber/15", ptsColor: "text-cyber" },
        2: { bar: "bg-gradient-to-b from-cyan-300 to-cyan-400", chipBorder: "border-cyan-300", chipText: "text-cyan-300", chipBg: "bg-cyan-300/15", ptsColor: "text-cyan-200" },
        3: { bar: "bg-gradient-to-b from-blaze to-orange-500", chipBorder: "border-blaze", chipText: "text-blaze", chipBg: "bg-blaze/15", ptsColor: "text-blaze" },
    };
    const s = RIBBON[row.rank];
    if (!isTop) {
        return (
            <div className={`flex items-center gap-3 px-3 py-2 rounded-xl border ${row.isMe ? "border-cyber/50 bg-cyber/[0.06]" : "border-white/[0.06] bg-[#141414]"}`}>
                <span className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold border border-white/10 text-zinc-500">#{row.rank}</span>
                <span className="text-sm font-bold text-zinc-300 flex-1">{row.name}</span>
                <span className="text-sm font-bold text-zinc-400">{row.pts} <span className="text-[9px] text-zinc-600">PTS</span></span>
            </div>
        );
    }
    return (
        <div className="relative flex items-stretch gap-0 overflow-hidden rounded-xl border border-white/[0.10] bg-[#141416]">
            <span className={`w-1.5 ${s.bar} shrink-0`} />
            <div className="flex items-center gap-3 flex-1 px-3 py-2.5">
                <span className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-black border-2 ${s.chipBorder} ${s.chipBg} ${s.chipText}`}>
                    #{row.rank}
                </span>
                <div className="flex-1 min-w-0">
                    <div className="font-black text-base text-white">{row.name}</div>
                    {row.streak >= 2 && <div className="flex items-center gap-1 text-[10px] text-orange-300 uppercase tracking-widest font-bold"><Flame className="w-3 h-3" /> W{row.streak}</div>}
                </div>
                <div className={`text-right ${s.ptsColor}`}>
                    <div className="text-[9px] uppercase tracking-widest opacity-70">PTS</div>
                    <div className="text-2xl font-black leading-none">{row.pts}</div>
                </div>
            </div>
        </div>
    );
}


function VariantCard({ label, subtitle, RowComponent, tone }) {
    return (
        <div className="brutal-card p-4 space-y-3 bg-ink">
            <div className="flex items-center gap-2 pb-2 border-b border-white/[0.08]">
                <Sparkles className={`w-4 h-4 ${tone}`} />
                <div>
                    <h3 className="font-display font-black text-lg uppercase tracking-tight">{label}</h3>
                    <p className="text-[11px] text-zinc-500">{subtitle}</p>
                </div>
            </div>
            <div className="space-y-2">
                {MOCK_ROWS.map((row) => (
                    <RowComponent key={row.rank} row={row} isTop={row.rank <= 3} />
                ))}
            </div>
        </div>
    );
}


export default function LeaderboardStylePreview() {
    const [pick, setPick] = useState(null);
    return (
        <div className="min-h-dvh bg-ink text-white p-4 sm:p-6">
            <div className="max-w-2xl mx-auto space-y-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="font-display font-black text-2xl uppercase tracking-tight">Leaderboard Variants</h1>
                        <p className="text-xs text-zinc-500">Pick the one that reads celebratory + TCR to you. Tap a card to lock in your pick.</p>
                    </div>
                    <Link to="/" className="text-xs text-cyber underline">← Back</Link>
                </div>

                {["A", "B", "C", "D"].map((key) => {
                    const CFG = {
                        A: { label: "A · Neon Circuit", subtitle: "Thin bright outline + electric glow, saturated rank chip", Row: NeonCircuitRow, tone: "text-[#00E5FF]" },
                        B: { label: "B · Retro Scoreboard", subtitle: "Solid color blocks + brutalist black borders, ESPN 90s vibe", Row: RetroScoreboardRow, tone: "text-cyber" },
                        C: { label: "C · Aurora Halo", subtitle: "Radial gradient behind row, crown/trophy icons, breathing #1", Row: AuroraHaloRow, tone: "text-yellow-300" },
                        D: { label: "D · Volt Podium Ribbon", subtitle: "Left-side vertical color ribbon + bright PTS number, restrained", Row: VoltPodiumRow, tone: "text-blaze" },
                    }[key];
                    return (
                        <button
                            key={key}
                            type="button"
                            onClick={() => setPick(key)}
                            data-testid={`variant-${key}-btn`}
                            className={`block w-full text-left transition-all ${pick === key ? "ring-2 ring-cyber ring-offset-2 ring-offset-ink rounded-2xl" : ""}`}
                        >
                            <VariantCard label={CFG.label} subtitle={CFG.subtitle} RowComponent={CFG.Row} tone={CFG.tone} />
                        </button>
                    );
                })}

                {pick && (
                    <div className="brutal-card p-4 bg-cyber/10 border-cyber text-center" data-testid="preview-pick-summary">
                        <p className="text-[10px] uppercase tracking-widest text-cyber font-bold mb-1">Your pick</p>
                        <p className="font-display font-black text-2xl">Variant {pick}</p>
                        <p className="text-xs text-zinc-400 mt-1">Send me "go with {pick}" and I'll wire it into the real Leaderboard.</p>
                    </div>
                )}
            </div>
        </div>
    );
}
